/**
 * Оболонка (UX-01; layout.md, розд. 1): верхня панель, ліва навігація, робоча область, права
 * панель, нижня навігація телефона. Оболонка нерухома й малюється один раз — під час переходів
 * змінюється лише `Outlet` робочої області.
 *
 * Тут лише композиція й стан, спільний для кількох областей. Кожна область — свій файл
 * (Topbar, SideNav, BottomBar, GlobalBanners, TodayAside, go-keys, палітра), тож тікети E3a правлять
 * свою область, а не цей файл. Склад навігації — nav-model.ts, з `/meta` за правами.
 */
import { useQuery } from '@tanstack/react-query';
import { Outlet, useMatchRoute, useRouterState } from '@tanstack/react-router';
import { lazy, Suspense, useMemo, useState } from 'react';
import { useIdlePrefetch } from '../app/preload.ts';
import { IncomingCallHost } from '../features/call/incoming/IncomingCallHost.tsx';
import { useTodayLive } from '../features/today/live.ts';
import { platformToday } from '../features/today/source.ts';
import { useRegionSync } from '../i18n/region.ts';
import { shell as t } from '../i18n/uk/shell.ts';
import { CommandHost } from '../palette/CommandHost.tsx';
import { useFirstScreenDone } from '../perf/first-screen.ts';
import { usePlatformEvents, usePreloadStreamShare } from '../platform/events.ts';
import { LifecycleKeys } from '../platform/LifecycleKeys.tsx';
import { QuickCreateHost } from '../platform/QuickCreateHost.tsx';
import { useReloginOpen } from '../platform/relogin.ts';
import { setPrefs, usePrefs } from '../prefs/prefs.ts';
import { RetryBoundary } from '../ui/RetryBoundary.tsx';
import { BottomBar } from './BottomBar.tsx';
import { GlobalBanners } from './GlobalBanners.tsx';
import { WIDE } from './geometry.ts';
import { GoHint, useGoKeys } from './go-keys.tsx';
import { normalizeNavMode, useWideWindow } from './nav-mode.ts';
import { MAIL_NAV_ID } from './nav-model.ts';
import { useRouteFocus } from './route-focus.ts';
import { SideNav } from './SideNav.tsx';
import { MAIN_ID, SkipLinks } from './skip-links.tsx';
import { TodayAside } from './TodayAside.tsx';
import { Topbar } from './Topbar.tsx';
import { TodayPanelContext } from './today-context.ts';
import { TopbarSlotContext } from './topbar-slot.tsx';
import { useShellNav } from './use-shell-nav.ts';
import { useWelcomeDue } from './use-welcome-due.ts';
import { meQuery, useShellUser } from './user.ts';

// Привітання першого входу — лінивий чанк (UX-38); вантажимо його лише за `welcomed === false`.
const Welcome = lazy(() => import('./Welcome.tsx').then((m) => ({ default: m.Welcome })));

const isWide = () => typeof window === 'undefined' || window.innerWidth >= WIDE;

export function AppShell() {
  const { nav, signedIn, metaReady, tasks } = useShellNav();
  // Поки сесія спливла (шар повторного входу), потоку подій немає; після входу — новий потік:
  // сервер закриває старий, а EventSource після 401 сам не перепідключається.
  const relogin = useReloginOpen();
  const prefs = usePrefs();
  // Легкий режим: потік — після межі першого екрана (perf/first-screen.ts), бюджет запитів ≤ 10.
  // Список там і так живе опитуванням, а не подіями (platform/list-live.ts). Звичайний режим
  // відкриває потік одразу: так подія між читанням списку й підключенням не загубиться.
  const firstScreen = useFirstScreenDone();
  const streamWhen = !relogin && (!prefs.lite || firstScreen);
  const streamWanted = metaReady && streamWhen;
  // Потік — за людиною (канал вкладок і звірка потоку, UX-24): `GET /me` паралельно з `/meta`, а
  // не після межі першого екрана, тож потік відкривається раніше (рев'ю Архітектора #1227, S7б); у
  // легкому режимі потік і так лише після неї. Чанк вкладки-лідера — теж паралельно (S9).
  const streamUser = useQuery({ ...meQuery(), enabled: signedIn && streamWhen }).data?.id;
  usePreloadStreamShare(signedIn && streamWhen);
  usePlatformEvents(streamWanted, streamUser);
  // Код палітри й наступних маршрутів — у простої після першого екрана (UX-33).
  useIdlePrefetch(signedIn);
  const user = useShellUser(signedIn);
  // Мова й пояс людини → формати дат і чисел (UX-42, UX-43). Екрани перемальовують себе самі:
  // корінь кожного викликає `useRegionKey()` (i18n/format.ts), перемонтування тут немає.
  useRegionSync(signedIn);
  const navMode = normalizeNavMode(prefs.nav);
  const wideWindow = useWideWindow();
  const matchRoute = useMatchRoute();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // Стартовий екран — стартова сторінка з `/meta` чи профілю («/» лише переадресовує туди).
  const onStart =
    Boolean(matchRoute({ to: '/' })) || (signedIn && nav.start !== null && pathname === nav.start);
  // «Сьогодні» — API модуля задач, лише після входу і з правом читати задачі.
  const todaySource = signedIn && tasks ? platformToday : null;
  useTodayLive(relogin ? null : todaySource);
  const todayHere = onStart && todaySource !== null;
  useRouteFocus();

  // < 1280 px панель накладна: за замовчуванням закрита, щоб не перекривати список.
  const [narrowOpen, setNarrowOpen] = useState(false);
  const todayOpen = isWide() ? prefs.todayOpen : narrowOpen;
  const today = useMemo(
    () => ({
      open: todayOpen,
      setOpen: (open: boolean) => (isWide() ? setPrefs({ todayOpen: open }) : setNarrowOpen(open)),
      source: todaySource,
      here: todayHere,
    }),
    [todayOpen, todaySource, todayHere],
  );

  const focusToday = () => {
    if (!todayOpen) today.setOpen(true);
    requestAnimationFrame(() =>
      document.querySelector<HTMLElement>('.today [data-roving][tabindex="0"]')?.focus(),
    );
  };
  const startLabel = nav.items.find((item) => item.to === nav.start)?.label;
  const homeLabel = nav.items.find((item) => item.to === nav.start)?.label ?? t.navHome;
  // Лише після входу в платформу. Чанк `Welcome`
  // вантажимо лише коли сервер каже `welcomed === false`; поки стан невідомий — нічого.
  const welcomeAllowed = Boolean(user?.signedIn && user.id && metaReady && !prefs.welcomeOff);
  const showWelcome = useWelcomeDue(welcomeAllowed);
  useGoKeys({
    onStart: todayHere,
    focusToday,
    homeLabel,
    mail: [...nav.items, ...(nav.more ?? [])].some((item) => item.id === MAIL_NAV_ID),
  });

  // Слот рядка застосунку для шляху картки (topbar-slot.tsx): елемент — у стані, щоб екран
  // перемалювався, коли панель змонтована.
  const [topbarSlot, setTopbarSlot] = useState<HTMLElement | null>(null);

  return (
    <TodayPanelContext.Provider value={today}>
      <TopbarSlotContext.Provider value={topbarSlot}>
        <div
          className="app"
          data-aside={todayHere && todayOpen ? 'open' : 'closed'}
          data-nav={navMode === 'auto' ? undefined : navMode}
        >
          <SkipLinks
            hasNav={nav.items.length > 0 || Boolean(nav.admin)}
            hasAside={todayHere && todayOpen}
          />
          <Topbar user={user} signedIn={signedIn} slotRef={setTopbarSlot} />
          <SideNav nav={nav} mode={navMode} wide={wideWindow} />
          <main id={MAIN_ID} className="main" tabIndex={-1}>
            <GlobalBanners />
            <Outlet />
          </main>
          {todayHere && todayOpen && <TodayAside onCollapse={() => today.setOpen(false)} />}
          <BottomBar nav={nav} />
          <GoHint />
          <CommandHost nav={nav} signedIn={signedIn} />
          <QuickCreateHost signedIn={signedIn} />
          {signedIn && <IncomingCallHost />}
          <LifecycleKeys signedIn={signedIn} />
          {showWelcome && (
            // Обрив мережі на `import()`: межа мовчки нічого не показує, а не віддає кореневому
            // `errorComponent` увесь застосунок.
            <RetryBoundary fallback={() => null}>
              <Suspense fallback={null}>
                <Welcome {...(startLabel ? { startLabel } : {})} />
              </Suspense>
            </RetryBoundary>
          )}
        </div>
      </TopbarSlotContext.Provider>
    </TodayPanelContext.Provider>
  );
}
